import React from "react";
import { Badge } from "@/components/ui/badge";
import type { ManuscriptMetadata } from "@/routes/innovation-days-demo/types";

interface BookDetailsContentProps {
  manuscript: ManuscriptMetadata;
}

// Calculate reading time (assuming ~250 words per minute)
function calculateReadingTime(wordCount: number): string {
  const minutes = Math.round(wordCount / 250);
  if (minutes < 60) return `${minutes} min read`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) return `${hours} hr read`;
  return `${hours}h ${remainingMinutes}m read`;
}

// Determine book length category
function getBookCategory(wordCount: number): string {
  if (wordCount < 7500) return "Short Story";
  if (wordCount < 20000) return "Novelette";
  if (wordCount < 50000) return "Novella";
  if (wordCount < 110000) return "Novel";
  return "Epic Novel";
}

export function BookDetailsContent({ manuscript }: BookDetailsContentProps) {
  const { word_count, genres, settings } = manuscript;
  const category = getBookCategory(word_count);
  const readingTime = calculateReadingTime(word_count);

  return (
    <div className="space-y-7 text-sm">
      {/* Word Count */}
      <div className="flex flex-col gap-[10px]">
        <div
          className="text-[12px] font-medium leading-[100%]"
          style={{ color: "#64748B" }}
        >
          Word Count
        </div>
        <div className="flex flex-wrap items-center gap-[10px]">
          <Badge className="w-auto bg-[#F1F5F9] text-foreground rounded-full px-[10px] py-1 border-transparent text-[12px] leading-[16px] font-medium">
            {word_count.toLocaleString()} Words
          </Badge>
          <Badge className="w-auto bg-[#EEF2FF] text-[#6366F1] rounded-full px-[10px] py-1 border-transparent text-[12px] leading-[16px] font-medium">
            {category}
          </Badge>
          <Badge className="w-auto bg-[#F1F5F9] text-foreground rounded-full px-[10px] py-1 border-transparent text-[12px] leading-[16px] font-medium">
            {readingTime}
          </Badge>
        </div>
      </div>
      {/* Genre */}
      <div className="flex flex-col gap-[10px]">
        <div
          className="text-[12px] font-medium leading-[100%]"
          style={{ color: "#64748B" }}
        >
          Genre
        </div>
        <div className="flex flex-wrap gap-[10px]">
          {genres.map((g) => (
            <Badge
              key={g}
              className="w-auto bg-[#F1F5F9] text-foreground rounded-full px-[10px] py-1 border-transparent text-[12px] leading-[16px] font-medium"
            >
              {g}
            </Badge>
          ))}
        </div>
      </div>
      {/* World and Setting */}
      <div className="flex flex-col gap-[10px]">
        <div
          className="text-[12px] font-medium leading-[100%]"
          style={{ color: "#64748B" }}
        >
          World and Setting
        </div>
        <div className="flex flex-wrap gap-[10px]">
          {settings
            .filter((setting) => setting.setting_type === "primary")
            .flatMap((setting) =>
              setting.labels.map((label) => (
                <Badge
                  key={`${setting.setting_type}-${label}`}
                  className="w-auto bg-[#F1F5F9] text-foreground rounded-full px-[10px] py-1 border-transparent text-[12px] leading-[16px] font-medium"
                >
                  {label}
                </Badge>
              )),
            )}
        </div>
      </div>
    </div>
  );
}
